import * as Sentry from '@sentry/react';

import { env } from 'config';
import { isReloadingForStaleChunk, isStaleChunkError } from 'utils/staleChunk';

const scrubPii = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  if (event.request?.headers) {
    const headers = { ...event.request.headers };
    if (headers.Authorization) headers.Authorization = '[Filtered]';
    if (headers.authorization) headers.authorization = '[Filtered]';
    if (headers.Cookie) delete headers.Cookie;
    if (headers.cookie) delete headers.cookie;
    event.request.headers = headers;
  }

  if (event.request?.url) {
    event.request.url = event.request.url.replace(/([?&](?:token|access_token|api_key|key)=)[^&#]+/gi, '$1[Filtered]');
  }

  return event;
};

export const handleStaleChunk = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null => {
  const isStaleChunk =
    isStaleChunkError(hint.originalException) || isStaleChunkError(event.exception?.values?.[0]?.value);

  if (!isStaleChunk) {
    return event;
  }

  if (isReloadingForStaleChunk()) {
    return null;
  }

  return { ...event, fingerprint: ['stale-chunk-import'] };
};

const isDefaultFingerprint = (part: string): boolean => part.replace(/\s/g, '') === '{{default}}';

export const issueKey = (event: Sentry.ErrorEvent): string => {
  const fingerprint = (event.fingerprint ?? []).map(String);
  const custom = fingerprint.filter((part) => !isDefaultFingerprint(part));
  if (custom.length > 0 && custom.length === fingerprint.length) {
    return JSON.stringify(['fingerprint', ...custom]);
  }

  const exceptions = event.exception?.values ?? [];
  const raised = exceptions[exceptions.length - 1];
  if (raised) {
    // The browser SDK marks every frame in_app and names are minified, so frames are told apart by position
    const frames = (raised.stacktrace?.frames ?? []).map(
      (frame) => `${frame.filename ?? ''}:${frame.lineno ?? ''}:${frame.colno ?? ''}`
    );
    return JSON.stringify(['exception', raised.module ?? null, raised.type ?? null, frames, ...custom]);
  }

  return JSON.stringify(['message', event.logger ?? null, event.logentry?.message ?? event.message ?? null, ...custom]);
};

type ErrorLimits = {
  perKey: number;
  total: number;
  windowMs: number;
  now?: () => number;
};

const withLimitTag = (event: Sentry.ErrorEvent, limit: 'reached' | 'total_reached'): Sentry.ErrorEvent => ({
  ...event,
  tags: { ...event.tags, error_limit: limit },
});

export const createErrorLimiter = ({ perKey, total, windowMs, now = () => performance.now() }: ErrorLimits) => {
  let windowStart = -Infinity;
  let sent = 0;
  let keys = new Map<string, { start: number; count: number }>();

  return (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    try {
      const time = now();
      if (time - windowStart >= windowMs) {
        windowStart = time;
        sent = 0;
        keys = new Map([...keys].filter(([, state]) => time - state.start < windowMs));
      }

      const key = issueKey(event);
      const state = keys.get(key);
      const current = state && time - state.start < windowMs ? state : { start: time, count: 0 };
      if (current.count >= perKey || sent >= total) {
        return null;
      }

      keys.set(key, { start: current.start, count: current.count + 1 });
      sent += 1;
      // The last event Sentry gets before a cap drops the rest, so it says which cap
      if (sent === total) {
        return withLimitTag(event, 'total_reached');
      }
      return current.count + 1 === perKey ? withLimitTag(event, 'reached') : event;
    } catch {
      return event;
    }
  };
};

// Per tab: a render or retry loop in one tab must not send (and bill) thousands of the same error
const ERROR_LIMITS: ErrorLimits = { perKey: 10, total: 50, windowMs: 60 * 60 * 1000 };

const PRODUCTION_ENVIRONMENTS = new Set(['uk-prod', 'prod-uk']);

const warnInvalid = (message: string): void => {
  // oxlint-disable-next-line no-console
  console.warn(`[sentry] ${message}`);
};

const parseRate = (name: string, value: string | undefined, fallback: number): number => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return fallback;
  }

  const rate = Number(trimmed);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    warnInvalid(`ignoring ${name}=${JSON.stringify(trimmed)}, expected a rate from 0 to 1; using ${fallback}`);
    return fallback;
  }
  return rate;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toUrlPrefix = (target: string): string | null => {
  if (target.startsWith('/')) {
    return target;
  }
  try {
    const url = new URL(target);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
};

// The SDK matches string targets as substrings anywhere in the URL, so anchor each one to make it a prefix.
const parseTracePropagationTargets = (value: string | undefined): RegExp[] | undefined => {
  const targets = (value ?? '')
    .split(',')
    .map((target) => target.trim())
    .filter(Boolean)
    .flatMap((target) => {
      const prefix = toUrlPrefix(target);
      if (prefix === null) {
        warnInvalid(
          `ignoring REACT_APP_SENTRY_TRACE_PROPAGATION_TARGETS entry ${JSON.stringify(target)}, expected an http(s) URL or a path starting with /`
        );
        return [];
      }
      return [new RegExp(`^${escapeRegExp(prefix)}`)];
    });

  return targets.length ? targets : undefined;
};

export const initSentry = (): void => {
  if (!env.REACT_APP_SENTRY_DSN || env.REACT_APP_LOGGER_HANDLER !== 'sentry') {
    return;
  }

  const environment = env.REACT_APP_SENTRY_ENVIRONMENT || 'unknown';
  const release = env.REACT_APP_VERSION || undefined;
  const tracePropagationTargets = parseTracePropagationTargets(env.REACT_APP_SENTRY_TRACE_PROPAGATION_TARGETS);
  const limitErrors = createErrorLimiter(ERROR_LIMITS);

  Sentry.init({
    dsn: env.REACT_APP_SENTRY_DSN,
    environment,
    release,
    sendDefaultPii: false,
    integrations: [
      // TODO: upgrade to reactRouterV6BrowserTracingIntegration once the
      // MobX-synced Router (App.tsx) is reconciled with Sentry's v6 router
      // hooks (useLocation / useNavigationType / matchRoutes).
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: parseRate(
      'REACT_APP_SENTRY_TRACES_SAMPLE_RATE',
      env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
      PRODUCTION_ENVIRONMENTS.has(environment) ? 0.1 : 1.0
    ),
    replaysSessionSampleRate: parseRate(
      'REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
      env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0
    ),
    replaysOnErrorSampleRate: parseRate(
      'REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE',
      env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0
    ),
    ...(tracePropagationTargets && { tracePropagationTargets }),
    ignoreErrors: [
      'timeout of 10000ms exceeded',
      'Network Error',
      'Request aborted',
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
    ],
    beforeSend: (event, hint) => {
      const scrubbed = scrubPii(event);
      const handled = scrubbed === null ? null : handleStaleChunk(scrubbed, hint);
      return handled === null ? null : limitErrors(handled);
    },
  });
};
